import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart2, TrendingUp, Users, GraduationCap, IndianRupee, 
  Calendar, FileText, Award, Building2, RefreshCw, Download,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [stats, setStats] = useState({
    users: { total: 0, candidates: 0, institutions: 0, examiners: 0, newThisPeriod: 0 },
    applications: { total: 0, pending: 0, approved: 0, rejected: 0 },
    payments: { total: 0, revenue: 0, pendingAmount: 0 },
    registrations: { total: 0, masterclass: 0, examination: 0, revenue: 0 },
    certificates: { total: 0, issued: 0 },
    examCenters: { total: 0, active: 0 }
  });
  const [recentActivity, setRecentActivity] = useState([]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data.stats || stats);
      setRecentActivity(res.data.recent_activity || []);
    } catch (err) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAnalytics();
  }, [token, period]);

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  const TrendIndicator = ({ value, positive = true }) => {
    if (value === 0) return <Minus className="h-4 w-4 text-slate-400" />;
    return positive ? (
      <span className="flex items-center text-green-600 text-xs">
        <ArrowUpRight className="h-3 w-3" /> {value}%
      </span>
    ) : (
      <span className="flex items-center text-red-600 text-xs">
        <ArrowDownRight className="h-3 w-3" /> {value}%
      </span>
    );
  };

  const [exporting, setExporting] = useState(false);

  const exportReport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/analytics/export-pdf?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `IMCAC_Analytics_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (err) {
      toast.error('Failed to export PDF report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="analytics-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A4A]">Analytics & Reports</h2>
          <p className="text-sm text-slate-500">Platform performance and insights</p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 rounded-sm">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} className="rounded-sm">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={exportReport} disabled={exporting} className="rounded-sm" data-testid="analytics-export-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
          <p className="text-sm text-slate-500 mt-2">Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  <TrendIndicator value={8} positive />
                </div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{stats.users.total}</p>
                <p className="text-xs text-slate-500">Total Users</p>
                <p className="text-xs text-blue-600 mt-1">+{stats.users.newThisPeriod} this period</p>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <IndianRupee className="h-5 w-5 text-green-600" />
                  <TrendIndicator value={15} positive />
                </div>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.payments.revenue)}</p>
                <p className="text-xs text-green-600">Total Revenue</p>
                <p className="text-xs text-amber-600 mt-1">{formatCurrency(stats.payments.pendingAmount)} pending</p>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <GraduationCap className="h-5 w-5 text-purple-600" />
                  <TrendIndicator value={12} positive />
                </div>
                <p className="text-2xl font-bold text-purple-700">{stats.registrations.total}</p>
                <p className="text-xs text-purple-600">Program Registrations</p>
                <p className="text-xs text-slate-500 mt-1">{stats.registrations.examination} examination registrations</p>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Award className="h-5 w-5 text-amber-600" />
                  <TrendIndicator value={5} positive />
                </div>
                <p className="text-2xl font-bold text-amber-700">{stats.certificates.issued}</p>
                <p className="text-xs text-amber-600">Certificates Issued</p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Stats Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {/* Users Breakdown */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#C5A572]" /> User Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Candidates</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500" style={{ width: `${(stats.users.candidates / stats.users.total) * 100}%` }}></div>
                      </div>
                      <span className="text-sm font-medium w-10 text-right">{stats.users.candidates}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Institutions</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{ width: `${(stats.users.institutions / stats.users.total) * 100}%` }}></div>
                      </div>
                      <span className="text-sm font-medium w-10 text-right">{stats.users.institutions}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Examiners</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500" style={{ width: `${(stats.users.examiners / stats.users.total) * 100}%` }}></div>
                      </div>
                      <span className="text-sm font-medium w-10 text-right">{stats.users.examiners}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Applications Status */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#C5A572]" /> Applications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-slate-50 rounded-sm">
                    <p className="text-2xl font-bold text-[#1B2A4A]">{stats.applications.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-sm">
                    <p className="text-2xl font-bold text-amber-600">{stats.applications.pending}</p>
                    <p className="text-xs text-amber-600">Pending</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-sm">
                    <p className="text-2xl font-bold text-green-600">{stats.applications.approved}</p>
                    <p className="text-xs text-green-600">Approved</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-sm">
                    <p className="text-2xl font-bold text-red-600">{stats.applications.rejected}</p>
                    <p className="text-xs text-red-600">Rejected</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Exam Centers */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#C5A572]" /> Exam Centers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-[#1B2A4A]">{stats.examCenters.total}</p>
                    <p className="text-xs text-slate-500">Registered</p>
                  </div>
                  <div className="w-px h-12 bg-slate-200"></div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{stats.examCenters.active}</p>
                    <p className="text-xs text-green-600">Active</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Utilization Rate</span>
                    <span className="font-medium text-[#1B2A4A]">
                      {stats.examCenters.total > 0 ? Math.round((stats.examCenters.active / stats.examCenters.total) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue & Recent Activity */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Revenue Breakdown */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-[#C5A572]" /> Revenue Sources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-white rounded-sm">
                    <div>
                      <p className="font-medium text-[#1B2A4A]">Examination Fees</p>
                      <p className="text-xs text-slate-500">{stats.registrations.examination} registrations</p>
                    </div>
                    <p className="text-lg font-bold text-blue-600">
                      {formatCurrency(stats.registrations.revenue)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-white rounded-sm">
                    <div>
                      <p className="font-medium text-[#1B2A4A]">Pending Payments</p>
                      <p className="text-xs text-slate-500">Awaiting confirmation</p>
                    </div>
                    <p className="text-lg font-bold text-amber-600">
                      {formatCurrency(stats.payments.pendingAmount)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-white rounded-sm">
                    <div>
                      <p className="font-medium text-[#1B2A4A]">Payment Transactions</p>
                      <p className="text-xs text-slate-500">{stats.payments.total} transactions</p>
                    </div>
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(stats.payments.revenue)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart2 className="h-4 w-4 text-[#C5A572]" /> Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentActivity.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-6">No recent activity yet.</p>
                  )}
                  {recentActivity.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded-sm">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        activity.type === 'registration' ? 'bg-purple-100 text-purple-600' :
                        activity.type === 'payment' ? 'bg-green-100 text-green-600' :
                        activity.type === 'application' ? 'bg-blue-100 text-blue-600' :
                        'bg-amber-100 text-amber-600'
                      }`}>
                        {activity.type === 'registration' && <GraduationCap className="h-4 w-4" />}
                        {activity.type === 'payment' && <IndianRupee className="h-4 w-4" />}
                        {activity.type === 'application' && <FileText className="h-4 w-4" />}
                        {activity.type === 'certificate' && <Award className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1B2A4A]">{activity.text}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {activity.user && <span>{activity.user}</span>}
                          {activity.amount && <Badge variant="outline" className="text-xs">{activity.amount}</Badge>}
                          <span>{activity.time}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
